import React, { Suspense, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Link } from 'react-router-dom';
import { NumberParam, useQueryParam } from 'use-query-params';
import { Box, Button, Card, CardHeader, Chip, IconButton, List, Popover, Tooltip, } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { Add, Edit } from '@mui/icons-material';
import { typeEmail, typePhone } from 'components/mui/Formatters';
import { ListItemLink } from 'components/mui/ListItemLink';
import Currency from 'components/formaters/Currency';
import { isFProject } from 'config/project';
import { CompanyModel, CompanyType } from 'models/CompanyModel';
import useCompanyModals from 'modules/companies/hooks/useCompanyModals';
import useLedgerModals from 'modules/invoices/hooks/useLedgerModals';
import companyList from 'recoil/atoms/companyList';
import auth from 'recoil/atoms/auth';


const CompaniesInner = () => {
    const companies = useRecoilValue(companyList(CompanyType.Shipper));
    const {
        openAddCompanyModal,
        openEditCompanyModal,
    } = useCompanyModals(CompanyType.Shipper);
    const {openTransactions} = useLedgerModals();
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const [id] = useQueryParam('id', NumberParam);

    const addButton = my.permissions.has('companies create') ? (
        <IconButton
            aria-label="add shipper"
            onClick={openAddCompanyModal}
        >
            <Add/>
        </IconButton>
    ) : null;

    const columns: GridColDef<CompanyModel>[] = [
        {
            field: 'id',
            headerName: '#',
            type: 'number',
            width: 30,
        },
        {
            field: 'name',
            headerName: t('name'),
            flex: 1,
            minWidth: 120,
            renderCell: params => (
                <div>
                    {params.value} {' '}
                    {params.row.alias &&
                        <Tooltip
                            title={params.row.alias}
                            placement="top"
                        >
                            <Chip
                                label="A"
                                size="small"
                            />
                        </Tooltip>
                    }
                </div>
            )
        },
        {
            field: 'email',
            headerName: t('email'),
            flex: 1,
            ...typeEmail,
            minWidth: 120,
        },
        {
            field: 'phone',
            headerName: t('phone'),
            flex: 1,
            ...typePhone,
            minWidth: 120,
        },
        ...my.permissions.has('invoices view') ? [
                {
                    field: 'freeBalance',
                    headerName: t('balance'),
                    type: 'number',
                    width: 110,
                    renderCell: params => (
                        <Tooltip
                            title={<>
                                {t('balance')}: <Currency value={params.row.balance}/><br/>
                                {t('frozen_in_invoice')}: <Currency value={params.row.balance - params.value}/>
                            </>}
                            placement="left"
                        >
                            <Button
                                variant="text"
                                color={params.value === 0 ? 'inherit' : params.value > 0 ? 'primary' : 'warning'}
                                onClick={() => openTransactions(params.row)}
                            >
                                <Currency value={params.value}/>
                            </Button>
                        </Tooltip>
                    ),
                },
            ] as GridColDef<CompanyModel>[]: [],
        {
            field: 'city',
            headerName: t('city'),
            flex: 1,
            minWidth: 100,
        },
        {
            field: 'exporterCode',
            headerName: t('exporter_code'),
            flex: 1,
        },

        ...isFProject ? [
            {
                field: 'shipperCode',
                headerName: t('shipper_code'),
                flex: 1,
                minWidth: 100,
            },
        ] : [
            {
                field: 'carsCount',
                headerName: t('cars'),
                width: 100,
                valueGetter: params => Object.values(params.row.carsCount).reduce((acc, value) => acc + value, 0),
                renderCell: params => <CarsCount
                    sum={params.value}
                    counts={params.row.carsCount}
                    companyId={params.row.id}
                />,
            },
            {
                field: 'usersCount',
                headerName: t('users'),
                width: 100,
                renderCell: params => (
                    <Chip
                        sx={{cursor: 'pointer'}}
                        label={params.value}
                        color={params.value > 1 ? 'info' : undefined}
                        variant={params.value > 1 ? 'outlined' : undefined}
                        size="small"
                        component={Link}
                        to={`/users?company=${params.row.id}`}
                    />
                ),
            },
        ] as GridColDef<CompanyModel>[],
        {
            field: 'actions',
            type: 'actions',
            getActions: params => [
                <GridActionsCellItem
                    icon={<Edit/>}
                    label={t('edit')}
                    onClick={() => openEditCompanyModal(params.row)}
                />
            ],
        },
    ];

    return (
        <Card>
            <CardHeader
                title={t('shippers')}
                action={addButton}
            />
            <Box sx={{height: 'calc(100vh - 196px)', padding: '0 1rem'}}>
                <DataGrid
                    rows={companies}
                    columns={columns}
                    initialState={{
                        filter: id ? {
                            filterModel: {
                                items: [{field: 'id', operator: '=', value: id}],
                            },
                        } : undefined,
                    }}
                />
            </Box>
        </Card>
    );
};


type CarsCountProps = {
    sum: number,
    counts: CompanyModel['carsCount'],
    companyId: number,
}

const CarsCount = ({sum, counts, companyId}: CarsCountProps) => {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const anchor = useRef(null);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    return (
        <>
            <Chip
                ref={anchor}
                label={sum}
                color={sum > 1 ? 'info' : undefined}
                variant={sum > 1 ? 'outlined' : undefined}
                size="small"
                onMouseEnter={handleOpen}
                onMouseLeave={handleClose}
            />
            <Popover
                id="cars-popover"
                open={open}
                anchorEl={anchor.current}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={handleClose}
                disableRestoreFocus
                sx={{
                    pointerEvents: 'none',

                    '& .MuiPaper-root': {pointerEvents: 'auto'},
                }}
                slotProps={{paper: {onMouseEnter: handleOpen, onMouseLeave: handleClose}}}
            >
                <List>
                    {Object.entries(counts).map(([status, count]) => <ListItemLink
                        key={status}
                        primary={t(`status_${status}`)}
                        endAddon={<Chip label={count} size="small"/>}
                        endAddonMargin="0 0 0 10px"
                        to={`/cars/${status}?shipper=${companyId}`}
                    />)}
                </List>
            </Popover>
        </>
    )
}

const Companies = () => {
    return (
        <Suspense fallback={null}>
            <CompaniesInner/>
        </Suspense>
    )
}

export default Companies;
